import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, ShoppingCart, Loader2, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useScrollLock } from '../hooks/useScrollLock';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';

// Utility: Format price in Colombian Pesos (COP)
const formatCOP = (price) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })?.format(price);
};

// Utility: Sort Talla (size) values in logical Colombian order
const sortTallaValues = (values) => {
  const sizeOrder = {
    'XS': 1,
    'S': 2,
    'M': 3,
    'L': 4,
    'XL': 5,
    'XXL': 6,
    'XXXL': 7,
    '2XL': 6,
    '3XL': 7
  };
  
  return [...values]?.sort((a, b) => {
    const aUpper = a?.toUpperCase()?.trim();
    const bUpper = b?.toUpperCase()?.trim();
    
    const aPriority = sizeOrder?.[aUpper];
    const bPriority = sizeOrder?.[bUpper];
    
    if (aPriority !== undefined && bPriority !== undefined) {
      return aPriority - bPriority;
    }
    
    if (aPriority !== undefined) return -1;
    if (bPriority !== undefined) return 1;
    
    return 0;
  });
};

// Memoized color thumbnail button component
const ColorThumbnailButton = React.memo(({ value, isSelected, thumbnailUrl, onClick }) => {
  if (!thumbnailUrl) {
    return (
      <button
        onClick={onClick}
        className={`px-4 py-2 border-2 rounded-lg font-medium transition-all ${
          isSelected
            ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
        }`}
        aria-label={`Select color ${value}`}
      >
        {value}
      </button>
    );
  }
  
  return (
    <button
      onClick={onClick}
      className={`relative w-20 h-20 rounded-lg overflow-hidden border-3 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        isSelected
          ? 'border-blue-600 ring-2 ring-blue-600 ring-offset-2 shadow-lg'
          : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
      }`}
      aria-label={`Select color ${value}`}
      title={value}
    >
      <img
        src={thumbnailUrl}
        alt={`Color option: ${value}`}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      {isSelected && (
        <div className="absolute inset-0 bg-blue-600 bg-opacity-20 flex items-center justify-center">
          <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
        </div>
      )}
    </button>
  );
});

ColorThumbnailButton.displayName = 'ColorThumbnailButton';

// Memoized variation button component
const VariationButton = React.memo(({ value, isSelected, variationName, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 border-2 rounded-lg font-medium transition-all ${
        isSelected
          ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
      }`}
      aria-label={`Select ${variationName} ${value}`}
    >
      {value}
    </button>
  );
});

VariationButton.displayName = 'VariationButton';

const ProductModal = ({ isOpen, onClose, skuId, isEmbed = false, storeUrl, vtexAccountName, companyId }) => {
  const { t } = useTranslation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedSku, setSelectedSku] = useState(skuId);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [isRendered, setIsRendered] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  useScrollLock(isOpen);
  const abortControllerRef = useRef(null);
  const idleCallbackRef = useRef(null);

  // Get current SKU item based on selection
  const currentSkuItem = useMemo(() => {
    if (!product?.items) return null;
    return product?.items?.find(item => item?.itemId === selectedSku) || product?.items?.[0];
  }, [product, selectedSku]);

  // Get images for current SKU
  const currentImages = useMemo(() => {
    return currentSkuItem?.images || [];
  }, [currentSkuItem]);

  // Get pricing for current SKU
  const pricing = useMemo(() => {
    if (!currentSkuItem?.sellers?.[0]?.commertialOffer) return null;
    const offer = currentSkuItem?.sellers?.[0]?.commertialOffer;
    return {
      listPrice: offer?.ListPrice || 0,
      price: offer?.Price || 0,
      available: offer?.AvailableQuantity > 0
    };
  }, [currentSkuItem]);

  // Extract unique variation types dynamically
  const variationTypes = useMemo(() => {
    if (!product?.items || product?.items?.length <= 1) return [];
    
    const variationTypesMap = new Map();
    
    product?.items?.forEach(item => {
      const itemVariations = item?.variations || [];
      
      if (!Array.isArray(itemVariations)) return;
      
      itemVariations?.forEach(variationName => {
        if (!variationTypesMap?.has(variationName)) {
          variationTypesMap?.set(variationName, new Set());
        }
        
        const values = item?.[variationName];
        
        if (values) {
          if (Array.isArray(values)) {
            values?.forEach(val => {
              if (val) variationTypesMap?.get(variationName)?.add(val);
            });
          } else {
            variationTypesMap?.get(variationName)?.add(values);
          }
        }
      });
    });
    
    const types = [];
    variationTypesMap?.forEach((valuesSet, variationName) => {
      if (valuesSet?.size > 0) {
        let valuesArray = Array.from(valuesSet);
        
        if (variationName?.toLowerCase() === 'talla') {
          valuesArray = sortTallaValues(valuesArray);
        }
        
        types?.push({
          name: variationName,
          values: valuesArray
        });
      }
    });
    
    return types;
  }, [product]);

  // Get image thumbnail for a specific color variation value
  const getColorThumbnail = useCallback((colorValue) => {
    if (!product?.items) return null;
    
    const matchingItem = product?.items?.find(item => {
      const itemColorValue = item?.Color || item?.color;
      
      if (Array.isArray(itemColorValue)) {
        return itemColorValue?.includes(colorValue);
      }
      return itemColorValue === colorValue;
    });
    
    if (matchingItem?.images?.[0]?.imageUrl) {
      return matchingItem?.images?.[0]?.imageUrl;
    }
    
    return null;
  }, [product?.items]);

  // Fetch product data immediately when modal opens
  useEffect(() => {
    if (!isOpen || !skuId) return;

    fetchProductData();

    return () => {
      // Abort any in-flight fetch when skuId/isOpen changes
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
        abortControllerRef.current = null;
      }
    };
  }, [isOpen, skuId]);

  // Defer non-critical render work
  useEffect(() => {
    if (isOpen && !isRendered) {
      if (window.requestIdleCallback) {
        const id = window.requestIdleCallback(() => setIsRendered(true));
        idleCallbackRef.current = { id, type: 'idle' };
      } else {
        const id = setTimeout(() => setIsRendered(true), 1);
        idleCallbackRef.current = { id, type: 'timeout' };
      }
    }
    return () => {
      if (idleCallbackRef?.current) {
        const { id, type } = idleCallbackRef.current;
        if (type === 'idle' && window.cancelIdleCallback) {
          window.cancelIdleCallback(id);
        } else if (type === 'timeout') {
          clearTimeout(id);
        }
        idleCallbackRef.current = null;
      }
    };
  }, [isOpen, isRendered]);

  // Cleanup on unmount: abort fetch, destroy swiper, clear state
  useEffect(() => {
    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
        abortControllerRef.current = null;
      }
      if (thumbsSwiper && !thumbsSwiper?.destroyed) {
        thumbsSwiper?.destroy(true, true);
      }
    };
  }, []);

  // Destroy thumbsSwiper when modal closes to prevent detached DOM nodes
  useEffect(() => {
    if (!isOpen) {
      if (thumbsSwiper && !thumbsSwiper?.destroyed) {
        thumbsSwiper?.destroy(true, true);
      }
      setThumbsSwiper(null);
      // Clear large product data when modal closes
      setProduct(null);
      setIsRendered(false);
      setQuantity(1);
      setSelectedVariations({});
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const fetchProductData = async () => {
    if (!skuId) return;

    // Abort any previous in-flight request
    if (abortControllerRef?.current) {
      abortControllerRef?.current?.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      const companyFallbackUrl = vtexAccountName ? `https://${vtexAccountName}.myvtex.com` : null;
      const baseStoreUrl = (storeUrl || companyFallbackUrl)?.replace(/\/$/, '') || '';

      const params = new URLSearchParams({ skuId });
      if (baseStoreUrl) {
        params.set('storeUrl', baseStoreUrl);
      } else if (companyId) {
        params.set('companyId', companyId);
      } else {
        throw new Error("No se pudo determinar la tienda VTEX para este producto");
      }

      const proxyUrl = `${supabaseUrl}/functions/v1/vtex-proxy?${params.toString()}`;

      const response = await fetch(proxyUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        signal: controller?.signal,
      });

      if (!response?.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = await response?.json();

      if (!Array.isArray(data) || data?.length === 0) {
        throw new Error("Producto no disponible");
      }

      const productData = data?.[0];

      setProduct(productData);
      const initialSku =
        productData.items.find(i => i.itemId === skuId)?.itemId ||
        productData.items[0]?.itemId;

      setSelectedSku(initialSku);

      // Inicializar variaciones
      const initialItem = productData?.items?.find(
        (item) => item?.itemId === skuId
      );

      if (initialItem?.variations) {
        const initialVariations = {};

        initialItem?.variations?.forEach((variationName) => {
          const value = initialItem?.[variationName];
          if (value) {
            initialVariations[variationName] = Array.isArray(value)
              ? value?.[0]
              : value;
          }
        });

        setSelectedVariations(initialVariations);
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Error fetching product:", err);
        setError("No se pudo cargar el producto");
        setProduct(null);
      }
    } finally {
      // Only update loading if this controller is still the active one
      if (abortControllerRef?.current === controller) {
        setLoading(false);
      }
    }
  };


  const handleVariationChange = (variationType, value) => {
    const newVariations = { ...selectedVariations, [variationType]: value };
    setSelectedVariations(newVariations);
    
    // Find matching SKU based on selected variations
    const matchingSku = product?.items?.find(item => {
      // Check if this item matches ALL selected variation values
      return Object.keys(newVariations)?.every(varName => {
        const selectedValue = newVariations?.[varName];
        const itemValue = item?.[varName];
        
        // Handle both string and array values from the item
        if (Array.isArray(itemValue)) {
          return itemValue?.includes(selectedValue);
        }
        return itemValue === selectedValue;
      });
    });
    
    if (matchingSku) {
      setSelectedSku(matchingSku?.itemId);
    }
  };

  const handleBuyClick = () => {
    const message = {
      action: 'addProductoFromCatalogFlip',
      data: {
        skuId: selectedSku,
        quantity: quantity
      }
    };

    if (isEmbed) {
      window.parent?.postMessage(message, '*');
    } else {
      window.postMessage(message, '*');
    }

    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  const incrementQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decrementQuantity = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e?.target?.value, 10);
    if (!isNaN(value) && value >= 1) {
      setQuantity(value);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-modal-title"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Close modal"
        >
          <X size={20} className="text-gray-600" />
        </button>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <Loader2 size={48} className="text-blue-600 animate-spin mb-4" />
            <p className="text-gray-600 text-lg">Cargando información del producto...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <AlertCircle size={48} className="text-red-500 mb-4" />
            <p className="text-gray-800 text-lg font-semibold mb-2">Oops!</p>
            <p className="text-gray-600 text-center">{error}</p>
            <button
              onClick={fetchProductData}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Product Content */}
        {product && !loading && !error && (
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Image Carousel Section */}
              <div className="flex-shrink-0 w-full lg:w-1/2">
                {currentImages?.length > 0 ? (
                  <div className="space-y-4">
                    {/* Main Image Carousel */}
                    <Swiper
                      modules={[Navigation, Pagination, Thumbs]}
                      navigation={{
                        prevEl: '.swiper-button-prev-custom',
                        nextEl: '.swiper-button-next-custom',
                      }}
                      pagination={{ clickable: true }}
                      thumbs={{ swiper: thumbsSwiper && !thumbsSwiper?.destroyed ? thumbsSwiper : null }}
                      className="rounded-lg overflow-hidden relative"
                      spaceBetween={10}
                    >
                      {currentImages?.map((image, index) => (
                        <SwiperSlide key={index}>
                          <img
                            src={image?.imageUrl}
                            alt={image?.imageText || `${product?.productName} - Image ${index + 1}`}
                            className="w-full h-auto object-cover rounded-lg"
                          />
                        </SwiperSlide>
                      ))}
                      
                      {/* Custom Navigation Buttons */}
                      {currentImages?.length > 1 && (
                        <>
                          <button
                            className="swiper-button-prev-custom absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                            aria-label="Previous image"
                          >
                            <ChevronLeft size={20} className="text-gray-800" />
                          </button>
                          <button
                            className="swiper-button-next-custom absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                            aria-label="Next image"
                          >
                            <ChevronRight size={20} className="text-gray-800" />
                          </button>
                        </>
                      )}
                    </Swiper>

                    {/* Thumbnail Carousel */}
                    {currentImages?.length > 1 && (
                      <Swiper
                        modules={[Thumbs]}
                        onSwiper={setThumbsSwiper}
                        spaceBetween={10}
                        slidesPerView={4}
                        watchSlidesProgress
                        className="thumbs-swiper"
                      >
                        {currentImages?.map((image, index) => (
                          <SwiperSlide key={index} className="cursor-pointer">
                            <img
                              src={image?.imageUrl}
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-20 object-cover rounded-lg border-2 border-transparent hover:border-blue-500 transition-colors"
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
                    <p className="text-gray-500">No image available</p>
                  </div>
                )}
              </div>

              {/* Product Info Section */}
              <div className="flex-1 flex flex-col">
                <h2 id="product-modal-title" className="text-3xl font-bold text-gray-900 mb-3">
                  {product?.productName}
                </h2>

                {product?.description && (
                  <div className="mb-6">
                    <div
                      className={`text-gray-600 text-sm leading-relaxed overflow-hidden transition-all duration-300 ${descriptionExpanded ? '' : 'line-clamp-3'}`}
                      dangerouslySetInnerHTML={{ __html: product?.description }}
                    />
                    <button
                      onClick={() => setDescriptionExpanded(prev => !prev)}
                      className="mt-1 text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1 transition-colors"
                    >
                      {descriptionExpanded ? 'Ver menos ↑' : 'Ver más ↓'}
                    </button>
                  </div>
                )}

                {/* Pricing Display */}
                {pricing && (
                  <div className="mb-6">
                    {pricing?.listPrice > pricing?.price ? (
                      <div className="flex items-baseline gap-3">
                        <span className="text-3xl font-bold text-green-600">
                          {formatCOP(pricing?.price)}
                        </span>
                        <span className="text-xl text-gray-500 line-through">
                          {formatCOP(pricing?.listPrice)}
                        </span>
                        <span className="text-sm bg-red-100 text-red-700 px-2 py-1 rounded-md font-semibold">
                          {Math.round(((pricing?.listPrice - pricing?.price) / pricing?.listPrice) * 100)}% OFF
                        </span>
                      </div>
                    ) : (
                      <span className="text-3xl font-bold text-gray-900">
                        {formatCOP(pricing?.price)}
                      </span>
                    )}
                    
                    {!pricing?.available && (
                      <p className="text-red-600 text-sm mt-2 font-medium">Out of stock</p>
                    )}
                  </div>
                )}

                {/* Dynamic Variation Selectors */}
                {variationTypes?.length > 0 && (
                  <div className="mb-6 space-y-4">
                    {variationTypes?.map((variationType) => {
                      const isColorVariation = variationType?.name?.toLowerCase() === 'color';
                      
                      return (
                        <div key={variationType?.name}>
                          <label className="block text-sm font-semibold text-gray-700 mb-2 capitalize">
                            {variationType?.name}:
                          </label>
                          {isColorVariation ? (
                            // Image thumbnail selector for Color variations
                            (<div className="flex flex-wrap gap-3">
                              {variationType?.values?.map((value) => {
                                const isSelected = selectedVariations?.[variationType?.name] === value;
                                const thumbnailUrl = getColorThumbnail(value);
                                
                                // Fallback to text if no image available
                                if (!thumbnailUrl) {
                                  return (
                                    <button
                                      key={value}
                                      onClick={() => handleVariationChange(variationType?.name, value)}
                                      className={`px-4 py-2 border-2 rounded-lg font-medium transition-all ${
                                        isSelected
                                          ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                      }`}
                                      aria-label={`Select color ${value}`}
                                    >
                                      {value}
                                    </button>
                                  );
                                }
                                
                                return (
                                  <button
                                    key={value}
                                    onClick={() => handleVariationChange(variationType?.name, value)}
                                    className={`relative w-20 h-20 rounded-lg overflow-hidden border-3 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                      isSelected
                                        ? 'border-blue-600 ring-2 ring-blue-600 ring-offset-2 shadow-lg'
                                        : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                                    }`}
                                    aria-label={`Select color ${value}`}
                                    title={value}
                                  >
                                    <img
                                      src={thumbnailUrl}
                                      alt={`Color option: ${value}`}
                                      className="w-full h-full object-cover"
                                    />
                                    {isSelected && (
                                      <div className="absolute inset-0 bg-blue-600 bg-opacity-20 flex items-center justify-center">
                                        <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                                          <svg
                                            className="w-4 h-4 text-white"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path d="M5 13l4 4L19 7"></path>
                                          </svg>
                                        </div>
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>)
                          ) : (
                            // Text-based selector for all other variations
                            (<div className="flex flex-wrap gap-2">
                              {variationType?.values?.map((value) => {
                                const isSelected = selectedVariations?.[variationType?.name] === value;
                                return (
                                  <button
                                    key={value}
                                    onClick={() => handleVariationChange(variationType?.name, value)}
                                    className={`px-4 py-2 border-2 rounded-lg font-medium transition-all ${
                                      isSelected
                                        ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                    aria-label={`Select ${variationType?.name} ${value}`}
                                  >
                                    {value}
                                  </button>
                                );
                              })}
                            </div>)
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* SKU Display */}
                {currentSkuItem && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">Ref:</span> {currentSkuItem?.referenceId?.[0]?.Value || currentSkuItem?.itemId}
                    </p>
                  </div>
                )}

                {/* Quantity Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Cantidad:
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={decrementQuantity}
                      className="w-10 h-10 flex items-center justify-center border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={handleQuantityChange}
                      className="w-20 px-3 py-2 text-center border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      aria-label="Quantity"
                    />
                    <button
                      onClick={incrementQuantity}
                      className="w-10 h-10 flex items-center justify-center border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Buy Button */}
                <button
                  onClick={handleBuyClick}
                  disabled={!pricing?.available}
                  className={`w-full font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg ${
                    pricing?.available
                      ? 'bg-green-600 hover:bg-green-700 text-white' :'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <ShoppingCart size={20} />
                  <span>{pricing?.available ? 'Agregar al carrito' : 'Agotado'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductModal;